import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Pressable, ActivityIndicator } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useRouter } from "expo-router";
import { useCameraSearch } from "@visioni/a3s-dir/app";
import { useEnvironment } from "@src/config/environment-store";
import { directory } from "@src/config/runtime";
import { authenticatedFetch } from "@src/features/auth";
import { useAuthStore } from "@src/features/auth/auth-store";
import { useTenant } from "@src/features/tenants";
import { setLastUsedTenantIdSync } from "@src/features/tenants/last-used-tenant-store";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { FormInput } from "@src/components/FormInput";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";

export const CameraSearchBar = memo(() => {
  const { theme } = useUnistyles();
  const router = useRouter();
  const env = useEnvironment();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isEnabled = useBooleanFlag(FEATURE_FLAGS.CAMERA_SEARCH);
  const { selectedTenant } = useTenant();

  const [inputValue, setInputValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef(null);

  // Clickaway listener to close the dropdown
  useEffect(() => {
    if (!showDropdown) return;
    const handleMouseDown = (e: MouseEvent) => {
      const node = containerRef.current as unknown as HTMLElement | null;
      if (node && !node.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [showDropdown]);

  // Debounce the input value
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!inputValue.trim()) {
      setDebouncedValue("");
      setShowDropdown(false);
      return;
    }
    timerRef.current = setTimeout(() => {
      setDebouncedValue(inputValue.trim());
      setShowDropdown(true);
    }, 600);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [inputValue]);

  // Add the CAM prefix if it's not present
  const searchAssetId =
    debouncedValue ?
      debouncedValue.toUpperCase().startsWith("CAM_") ?
        debouncedValue
      : `CAM_${debouncedValue}`
    : undefined;

  const { data, isFetching } = useCameraSearch({
    assetId: searchAssetId,
    authenticatedFetch,
    directoryBaseUrl: directory(env),
    enabled: isAuthenticated && isEnabled && !!debouncedValue,
  });

  const handleResultPress = useCallback(
    (assetCameraKey: string) => {
      if (!data?.result) return;
      const { tenant, site } = data.result;
      setInputValue("");
      setShowDropdown(false);
      const cameraPath = `/tenants/${tenant}/sites/${site}/cameras/${assetCameraKey}`;
      if (selectedTenant?.id !== tenant) {
        setLastUsedTenantIdSync(tenant);
        const basePathPrefix =
          window.location.pathname === "/app" || window.location.pathname.startsWith("/app/") ? "/app" : "";
        window.location.pathname = `${basePathPrefix}${cameraPath}`;
      } else {
        router.push(cameraPath as never);
      }
    },
    [data, router, selectedTenant],
  );

  const handleClear = useCallback(() => {
    setInputValue("");
    setDebouncedValue("");
    setShowDropdown(false);
  }, []);

  const handleFocus = useCallback(() => {
    if (debouncedValue) setShowDropdown(true);
  }, [debouncedValue]);

  if (!isEnabled) return null;

  return (
    <View ref={containerRef} style={styles.container}>
      <View style={styles.inputContainer}>
        <FormInput
          placeholder="Search by Camera ID"
          value={inputValue}
          onChangeText={setInputValue}
          onFocus={handleFocus}
          iconLeft={<Icon name="search" size={16} color={theme.colors.textMuted} />}
          size="small"
          returnKeyType="search"
          style={styles.formInput}
          containerStyle={styles.formInputContainer}
          iconRight={
            inputValue ?
              <Pressable
                onPress={handleClear}
                style={styles.clearButton}
                accessibilityRole="button"
                accessibilityLabel="Clear search"
              >
                <Icon name="closeCircle" size={16} color={theme.colors.textMuted} />
              </Pressable>
            : undefined
          }
        />
      </View>
      {showDropdown && debouncedValue && (
        <View style={styles.dropdown}>
          {isFetching ?
            <View style={[styles.dropdownItem, styles.dropdownItemCenter]}>
              <ActivityIndicator size="small" color={theme.colors.textMuted} />
            </View>
          : data?.result && data.result.cameras.length > 0 ?
            <>
              {data.result.cameras.map((cam) => (
                <Pressable
                  key={cam.assetCameraKey}
                  onPress={() => handleResultPress(cam.assetCameraKey)}
                  style={styles.dropdownItem}
                >
                  <Icon name="video" size={16} color={theme.colors.icon} />
                  <View style={styles.resultText}>
                    <Text textStyle="sm/medium" color="text" numberOfLines={1}>
                      {cam.friendlyName ?? cam.camera}
                    </Text>
                    <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                      {cam.assetCameraKey}
                    </Text>
                  </View>
                  <Icon name="chevronRight" size={16} color={theme.colors.textMuted} />
                </Pressable>
              ))}
            </>
          : <View style={styles.dropdownItem}>
              <Text textStyle="xs/regular" color="textMuted">
                No camera found
              </Text>
            </View>
          }
        </View>
      )}
    </View>
  );
});

const styles = StyleSheet.create((theme) => ({
  container: {
    position: "relative",
    zIndex: 100,
    width: 240,
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    paddingLeft: theme.spacing.lg,
    gap: theme.spacing.xs,
    borderColor: theme.colors.border,
    backgroundColor: theme.colors.surface,
    overflow: "hidden",
  },
  formInput: {
    flex: 1,
    minWidth: 0,
  },
  formInputContainer: {
    borderWidth: 0,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  clearButton: {
    alignItems: "center",
    justifyContent: "center",
  },
  dropdown: {
    position: "absolute",
    left: 0,
    right: 0,
    top: "100%",
    marginTop: theme.spacing.xs,
    borderRadius: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    backgroundColor: theme.colors.surfaceCard,
    overflow: "hidden",
    zIndex: 1000,
    ...theme.shadows.md,
  },
  dropdownItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.sm,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    minHeight: 40,
  },
  dropdownItemCenter: {
    justifyContent: "center",
  },
  resultText: {
    flex: 1,
    gap: 2,
  },
}));
